/* ═════════════════════════════════════════════════════════════════
   Admin · Vinci Correction Pool
   ═════════════════════════════════════════════════════════════════
   Queue of partner-filed correction submissions. Admin can:
   * Preview merge (diff of what the change would mutate)
   * Verify & apply — commits the change to the ledger
   * Reject with reason
   ═════════════════════════════════════════════════════════════════ */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, XCircle, Eye, Download, FileText,
  ArrowRight, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCorrectionPool() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("submitted");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);   // detail modal payload

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/bureaucratic/corrections`, {
        params: { status: status || undefined }, withCredentials: true,
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load the pool");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F2F0EA" }}>
      <div className="border-b" style={{ background: "#1F2226", borderColor: "#4A5058" }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/bureaucratic" data-testid="pool-back-library"
                    className="flex items-center gap-1.5 text-[10px] font-grotesk uppercase tracking-[0.28em] text-[#C4C8CC] hover:text-white">
              <ArrowLeft size={12} /> Library
            </Link>
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#7A8590] font-mono">
                Vinci Executive Command
              </div>
              <h1 className="font-serif text-2xl text-[#E4E6E8] tracking-wide mt-1">
                Correction Pool · Compliance Capsule
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["submitted", "verified", "rejected", ""].map((s) => (
              <button key={s || "all"}
                      data-testid={`pool-filter-${s || "all"}`}
                      onClick={() => setStatus(s)}
                      className={`font-mono text-[9px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-sm border transition-colors ${
                        status === s
                          ? "bg-[#E4E6E8] text-black border-[#E4E6E8]"
                          : "border-[#C4C8CC]/40 text-[#C4C8CC] hover:bg-[#4A5058]/40"
                      }`}>
                {s || "All"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {busy ? (
          <div className="text-center py-24 text-[#6B6F76] font-mono text-sm">
            Loading queue…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-[#6B6F76] font-mono text-sm">
            No submissions in this bucket.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {items.map((it) => (
              <SubmissionRow key={it.id} it={it}
                              onOpen={() => setSelected(it)} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <SubmissionModal
          submission={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function SubmissionRow({ it, onOpen }) {
  const statusStyle = {
    submitted: { bg: "#3A4048", fg: "#E4E6E8", label: "Awaiting Verification" },
    verified:  { bg: "#1F3B2E", fg: "#7BD8AA", label: "Verified & Applied" },
    rejected:  { bg: "#40232A", fg: "#F2A6B1", label: "Rejected" },
  }[it.status] || { bg: "#3A4048", fg: "#E4E6E8", label: it.status };
  return (
    <div
      data-testid={`pool-row-${it.id}`}
      className="border bg-white/60 p-4 flex items-center justify-between hover:shadow-md transition-shadow"
      style={{ borderColor: "#C4C8CC" }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <FileText size={16} className="text-[#4A5058] shrink-0" />
        <div className="min-w-0">
          <div className="font-serif text-[15px] text-[#161A1E] truncate">
            {it.title}
          </div>
          <div className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.22em] text-[#6B6F76]">
            {it.submitter_email} · Ref {it.id.slice(-6).toUpperCase()} ·
            {" "}Filed {(it.created_at || "").slice(0, 10)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.28em] px-2.5 py-1 rounded-sm"
              style={{ background: statusStyle.bg, color: statusStyle.fg }}>
          {statusStyle.label}
        </span>
        <button onClick={onOpen}
                data-testid={`pool-open-${it.id}`}
                className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.28em] text-[#2C3138] hover:text-black">
          Open <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}

function SubmissionModal({ submission, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [merge, setMerge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/bureaucratic/corrections/${submission.id}`,
          { withCredentials: true });
        if (alive) setDetail(data);
      } catch (e) { toast.error("Unable to load detail"); }
    })();
    return () => { alive = false; };
  }, [submission.id]);

  const previewMerge = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/bureaucratic/corrections/${submission.id}/preview-merge`,
        {}, { withCredentials: true });
      setMerge(data.merge);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge preview failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!merge) { toast.message("Preview the merge before applying"); return; }
    if (!window.confirm("Apply this change to the Vinci ledger?")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/bureaucratic/corrections/${submission.id}/verify`,
        {}, { withCredentials: true });
      toast.success("Verified & applied · petitioner notified");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters"); return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/bureaucratic/corrections/${submission.id}/reject`,
        { reason: rejectReason.trim() }, { withCredentials: true });
      toast.success("Rejected · petitioner notified");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
    } finally { setBusy(false); }
  };

  const sub = detail?.submission || submission;
  const form = detail?.form;
  const uploads = sub.uploads || {};

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="pool-modal">
      <div className="max-w-4xl w-full max-h-[92vh] overflow-y-auto border bg-[#F2F0EA]"
           style={{ borderColor: "#4A5058" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b bg-[#1F2226]" style={{ borderColor: "#4A5058" }}>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.32em] text-[#7A8590]">
              Compliance Capsule · {sub.id.slice(-6).toUpperCase()}
            </div>
            <div className="font-serif text-lg text-[#E4E6E8]">{sub.title}</div>
          </div>
          <button onClick={onClose}
                  className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C4C8CC] hover:text-white">
            Close ✕
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Petitioner strap */}
          <div className="grid grid-cols-3 gap-4 text-[11px] font-mono uppercase tracking-[0.22em] text-[#6B6F76]">
            <div><div className="text-[#4A5058] mb-1">Petitioner</div><div className="text-[#161A1E]">{sub.submitter_email}</div></div>
            <div><div className="text-[#4A5058] mb-1">Filed</div><div className="text-[#161A1E]">{(sub.created_at || "").slice(0, 19).replace("T", " ")}</div></div>
            <div><div className="text-[#4A5058] mb-1">Status</div><div className="text-[#161A1E]">{sub.status}</div></div>
          </div>

          {/* Declared values */}
          {form && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#7A8590] mb-2">
                Declared Values
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                {form.fields.map((f) => (
                  <div key={f.key} className="border border-[#C4C8CC]/60 bg-white/60 p-2.5">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#6B6F76]">
                      {f.label}
                    </div>
                    <div className="mt-1 font-serif text-[#161A1E] whitespace-pre-wrap">
                      {sub.values?.[f.key] || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploads */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#7A8590] mb-2">
              Compliance Capsule · Uploads
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              {["id_upload", "proof_of_mistake", "covering_letter"].map((k) => {
                const u = uploads[k];
                return (
                  <div key={k} className="border border-[#C4C8CC]/60 bg-white/60 p-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#6B6F76]">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="mt-1 font-serif text-[#161A1E] truncate">
                      {u?.filename || "—"}
                    </div>
                    {u?.filename && (
                      <a href={`${API}/bureaucratic/corrections/${sub.id}/upload/${k}`}
                         target="_blank" rel="noreferrer"
                         className="mt-1.5 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[#2C3138] hover:text-black">
                        <Download size={10} /> Download
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Merge preview */}
          {merge && (
            <div className="border border-[#4A5058]/40 bg-white p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#4A5058] mb-2">
                Merge Preview · Operation "{merge.op}"
              </div>
              {(merge.diff || []).length === 0 ? (
                <div className="text-[12px] font-mono text-[#6B6F76]">
                  No ledger fields would change with this filing.
                </div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-[#6B6F76]">
                      <th className="text-left py-1.5">Field</th>
                      <th className="text-left py-1.5">Before</th>
                      <th className="text-left py-1.5">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merge.diff.map((d, i) => (
                      <tr key={i} className="border-t border-[#C4C8CC]/60">
                        <td className="py-1.5 font-mono text-[#4A5058]">{d.key}</td>
                        <td className="py-1.5 text-[#6B6F76]">{String(d.before)}</td>
                        <td className="py-1.5 text-[#161A1E] font-semibold">{String(d.after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Reject reason */}
          {showReject && (
            <div className="border border-[#B23A48]/50 bg-[#40232A]/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#B23A48] mb-2">
                Observations for Rejection
              </div>
              <textarea
                data-testid="pool-reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full bg-white border border-[#C4C8CC] px-3 py-2 text-[12px] text-[#161A1E]"
                placeholder="e.g. Bank letterhead not on official stationery; please re-upload with company seal." />
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="sticky bottom-0 px-5 py-4 border-t bg-[#1F2226] flex items-center justify-between"
             style={{ borderColor: "#4A5058" }}>
          <a href={`${API}/bureaucratic/corrections/${sub.id}/snapshot`}
             target="_blank" rel="noreferrer"
             data-testid="pool-open-snapshot"
             className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-[#C4C8CC] hover:text-white">
            <Eye size={12} /> Open PDF snapshot
          </a>
          <div className="flex items-center gap-2">
            {sub.status === "submitted" && (
              <>
                <button
                  data-testid="pool-preview-merge"
                  onClick={previewMerge}
                  disabled={busy}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] border border-[#C4C8CC]/40 text-[#E4E6E8] hover:bg-[#4A5058]/60 px-4 py-2 rounded-sm">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                  Preview Merge
                </button>
                <button
                  data-testid="pool-verify"
                  onClick={verify}
                  disabled={busy || !merge}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] bg-[#7BD8AA] text-black hover:brightness-110 px-4 py-2 rounded-sm disabled:opacity-40">
                  <ShieldCheck size={12} /> Verify & Apply
                </button>
                {!showReject ? (
                  <button
                    data-testid="pool-open-reject"
                    onClick={() => setShowReject(true)}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] border border-[#B23A48]/60 text-[#F2A6B1] hover:bg-[#40232A]/60 px-4 py-2 rounded-sm">
                    <XCircle size={12} /> Reject…
                  </button>
                ) : (
                  <button
                    data-testid="pool-confirm-reject"
                    onClick={reject} disabled={busy}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] bg-[#B23A48] text-white hover:brightness-110 px-4 py-2 rounded-sm">
                    <XCircle size={12} /> Confirm Reject
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
